/**
 * Form quick action component for surfacing agent forms in chat.
 * Provides visual variants: a suggestion row for the empty state
 * and a dropdown button for the input toolbar.
 * On submission, serializes form values into a readable text message.
 *
 * @module components/chat/form-quick-action
 */

import { useRef, useState } from "react";
import { ArrowUpLeft, ChevronUp, FileText, Loader2, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FormRenderer } from "@/components/agents/interfaces/form-renderer";
import { useAgentUis } from "@/hooks/use-agent-uis";
import type { AgentUiRecord, FormField } from "@/types/agent-ui-types";

interface FormQuickActionProps {
  /** The agent ID to fetch forms for */
  agentId: string;
  /** Callback invoked with the serialized form text on submission */
  onSubmit: (message: string) => void;
  /** Visual variant — "suggestion" for empty state, "toolbar" for input toolbar */
  variant?: "suggestion" | "toolbar";
  /** Whether the button should be disabled (e.g. while streaming) */
  disabled?: boolean;
}

/**
 * Serializes form values into a human-readable text message.
 * Each field is formatted as a labeled bullet point.
 */
function serializeFormValues(
  values: Record<string, unknown>,
  fields: FormField[]
): string {
  const lines: string[] = [];
  lines.push("Form submission:\n");
  const fieldMap = new Map(fields.map((f) => [f.id, f]));

  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === null || value === "") continue;
    const label = fieldMap.get(key)?.label ?? key;

    if (Array.isArray(value)) {
      lines.push(`- ${label}: ${value.join(", ")}`);
    } else if (typeof value === "boolean") {
      lines.push(`- ${label}: ${value ? "Yes" : "No"}`);
    } else {
      lines.push(`- ${label}: ${String(value)}`);
    }
  }

  return lines.join("\n");
}

/**
 * Form quick action — renders a trigger that opens a dialog with the agent's form.
 * Renders nothing if the agent has no form UIs.
 */
export function FormQuickAction({ agentId, onSubmit, variant = "toolbar", disabled = false }: FormQuickActionProps) {
  const { uis, isLoading } = useAgentUis(agentId);
  const [selectedUi, setSelectedUi] = useState<AgentUiRecord | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Don't render anything while loading or if no forms exist
  if (isLoading || uis.length === 0) return null;

  // For the suggestion variant, use the default UI
  const defaultUi = uis.find((ui) => ui.isDefault) ?? uis[0];

  /** Trigger form validation and submission via the hidden submit button */
  const handleSubmitClick = () => {
    formRef.current?.requestSubmit();
  };

  /** Handle FormRenderer's onSubmit — serialize and send as chat message */
  const handleFormSubmit = (values: Record<string, unknown>) => {
    if (!selectedUi) return;
    setIsSubmitting(true);
    const message = serializeFormValues(values, selectedUi.formSchema.fields);
    onSubmit(message);
    setIsSubmitting(false);
    setSelectedUi(null);
  };

  /** Open a specific form in the dialog */
  const openForm = (ui: AgentUiRecord) => {
    setSelectedUi(ui);
  };

  return (
    <>
      {variant === "suggestion" ? (
        <div className="w-full max-w-[700px] mt-6">
          <button
            onClick={() => openForm(defaultUi)}
            className="w-full flex items-center gap-3 py-4 px-2 text-left text-muted-foreground hover:text-foreground transition-colors group border-b border-border"
          >
            <FileText className="size-4 flex-shrink-0 opacity-60 group-hover:opacity-100" />
            <span className="text-sm truncate flex-1">{defaultUi.name}</span>
            <ArrowUpLeft className="size-4 opacity-50 group-hover:opacity-100 flex-shrink-0" />
          </button>
        </div>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Zap className="size-3.5" />
              <span>Quick Actions</span>
              <ChevronUp className="size-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-56">
            {uis.map((ui) => (
              <DropdownMenuItem
                key={ui.id}
                onClick={() => openForm(ui)}
                className="cursor-pointer gap-2"
              >
                <FileText className="size-3.5 text-muted-foreground" />
                <span>{ui.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <Dialog open={!!selectedUi} onOpenChange={(open) => { if (!open) setSelectedUi(null); }}>
        {selectedUi && (
          <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>{selectedUi.name}</DialogTitle>
              {selectedUi.description && (
                <DialogDescription>{selectedUi.description}</DialogDescription>
              )}
            </DialogHeader>

            <div className="flex-1 overflow-y-auto py-2">
              <FormRenderer
                key={selectedUi.id}
                schema={selectedUi.formSchema}
                onSubmit={handleFormSubmit}
                isSubmitting={isSubmitting}
                formRef={formRef}
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedUi(null)}>
                Cancel
              </Button>
              <Button onClick={handleSubmitClick} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  selectedUi.formSchema.submitButton?.label ?? "Submit"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}
